import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { calendarAPI } from '../services/api';
import './CalendarPage.css';
import Toast from '../components/Toast';

const CalendarPage = () => {
    const navigate = useNavigate();

    // ── Estados ──
    const [currentDate, setCurrentDate] = useState(new Date());
    const [events, setEvents] = useState([]);
    const [stats, setStats] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [toast, setToast] = useState(null);

    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showEventModal, setShowEventModal] = useState(false);
    const [dayListModal, setDayListModal] = useState({ isOpen: false, date: null, events: [] });

    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Detectar móvil
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // ── Carga de datos ──
    useEffect(() => {
        const controller = new AbortController();
        loadCalendarData(controller.signal);
        
        return () => {
            controller.abort();
        };
    }, [currentDate]);

    const loadCalendarData = async (signal) => {
        setIsLoading(true);
        try {
            const { start_date, end_date } = getDateRange();
            const [eventsRes, statsRes] = await Promise.all([
                calendarAPI.getEvents({ start_date, end_date }),
                calendarAPI.getStats({ start_date, end_date })
            ]);
            
            if (signal?.aborted) return; // ← Cancelar si hubo navegación rápida
            
            setEvents(eventsRes.data.data || []);
            setStats(statsRes.data.data || {});
        } catch (error) {
            if (signal?.aborted) return; // ← No mostrar error si simplemente abortamos navegación
            
            console.error('Error al cargar calendario:', error);
            setToast({ type: 'error', message: 'Error al cargar el cronograma. Verifica tu conexión.' });
        } finally {
            if (!signal?.aborted) {
                setIsLoading(false);
            }
        }
    };

    const getDateRange = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        return {
            start_date: new Date(year, month, 1).toISOString().split('T')[0],
            end_date: new Date(year, month + 1, 0).toISOString().split('T')[0]
        };
    };

    const navigateDate = (dir) => {
        const newDate = new Date(currentDate);
        newDate.setMonth(currentDate.getMonth() + dir);
        setCurrentDate(newDate);
    };

    const goToToday = () => setCurrentDate(new Date());

    // ── Filtrado ──
    const getFilteredEvents = () => {
        return events.filter(ev => {
            const matchStatus = statusFilter === 'all' || ev.status === statusFilter;
            const searchLower = searchTerm.toLowerCase();
            const matchSearch = !searchTerm ||
                ev.customer_name?.toLowerCase().includes(searchLower) ||
                ev.order_number?.toString().includes(searchLower);
            return matchStatus && matchSearch;
        });
    };

    const getEventsForDate = (date) => {
        if (!date) return [];
        const dateStr = date.toISOString().split('T')[0];
        return getFilteredEvents().filter(e => e.event_date && e.event_date.startsWith(dateStr));
    };

    // ── Modales ──
    const openEventModal = (event) => {
        setSelectedEvent(event);
        setShowEventModal(true);
    };

    const closeEventModal = () => {
        setShowEventModal(false);
        setSelectedEvent(null);
    };

    const openDayListModal = (date, dayEvents) => {
        setDayListModal({ isOpen: true, date, events: dayEvents });
    };

    const closeDayListModal = () => {
        setDayListModal({ isOpen: false, date: null, events: [] });
    };

    const handleEventClickFromList = (event) => {
        closeDayListModal();
        openEventModal(event);
    };

    // ── Helpers ──
    const getStatusColor = (status) => {
        const colors = {
            'pendiente': '#ffc107',
            'en_preparacion': '#17a2b8',
            'produccion': '#17a2b8',
            'listo': '#28a745',
            'entregado': '#6c757d',
            'cancelado': '#dc3545'
        };
        return colors[status] || '#6c757d';
    };

    const getStatusLabel = (status) => {
        const labels = {
            'pendiente': 'Pendiente',
            'en_preparacion': 'Producción',
            'produccion': 'Producción',
            'listo': 'Listo',
            'entregado': 'Entregado',
            'cancelado': 'Cancelado'
        };
        return labels[status] || status;
    };

    const getPriorityLabel = (priority) => {
        const map = {
            'overdue': { label: '🔴 Vencido', color: '#dc3545' },
            'today': { label: '⚡ Hoy', color: '#ffc107' },
            'tomorrow': { label: '📅 Mañana', color: '#17a2b8' },
            'scheduled': { label: '🗓 Programado', color: '#6c757d' }
        };
        return map[priority] || { label: priority, color: '#6c757d' };
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('es-PE', {
            weekday: 'short', day: '2-digit', month: 'short', timeZone: 'UTC'
        });
    };

    // ── Vista Mensual (PC) ──
    const renderMonthView = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const days = [];
        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="cal-day-cell empty" />);
        }

        for (let d = 1; d <= daysInMonth; d++) {
            const date = new Date(year, month, d);
            const dayEvents = getEventsForDate(date);
            const isToday = date.toDateString() === new Date().toDateString();

            days.push(
                <div key={d} className={`cal-day-cell ${isToday ? 'today' : ''}`}>
                    <div className="cal-day-header">
                        <span className={`cal-day-number ${isToday ? 'today-circle' : ''}`}>{d}</span>
                        {dayEvents.length > 0 && <span className="cal-day-badge">{dayEvents.length}</span>}
                    </div>
                    <div className="cal-day-body">
                        {dayEvents.slice(0, 2).map(ev => (
                            <div
                                key={ev.id}
                                className="cal-event-pill"
                                style={{ borderLeftColor: getStatusColor(ev.status) }}
                                onClick={(e) => { e.stopPropagation(); openEventModal(ev); }}
                                title={`${ev.customer_name} — ${getStatusLabel(ev.status)}`}
                            >
                                {ev.customer_name}
                            </div>
                        ))}
                        {dayEvents.length > 2 && (
                            <div
                                className="cal-more-events"
                                onClick={(e) => { e.stopPropagation(); openDayListModal(date, dayEvents); }}
                            >
                                +{dayEvents.length - 2} más
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        const filteredEvents = getFilteredEvents();
        const hasNoEvents = filteredEvents.length === 0 && !isLoading;

        return (
            <div style={{ position: 'relative' }}>
                <div className="cal-grid">{days}</div>
                {hasNoEvents && (
                    <div className="cal-empty-state" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.85)', borderRadius: '12px', zIndex: 5 }}>
                        <div className="cal-empty-icon" style={{ fontSize: '3rem', marginBottom: '10px' }}>📭</div>
                        <p style={{ fontSize: '1.2rem', color: '#555', fontWeight: 'bold' }}>Sin entregas programadas este mes</p>
                    </div>
                )}
            </div>
        );
    };

    // ── Vista Agenda (Móvil) ──
    const renderAgendaView = () => {
        const filtered = getFilteredEvents();

        if (filtered.length === 0) {
            return (
                <div className="cal-empty-state">
                    <div className="cal-empty-icon">📭</div>
                    <p>No hay pedidos programados este mes</p>
                </div>
            );
        }

        // Agrupar por fecha
        const grouped = filtered.reduce((acc, ev) => {
            const date = ev.event_date ? ev.event_date.split('T')[0] : 'Sin fecha';
            if (!acc[date]) acc[date] = [];
            acc[date].push(ev);
            return acc;
        }, {});

        const sortedDates = Object.keys(grouped).sort();
        const todayStr = new Date().toISOString().split('T')[0];

        return (
            <div className="cal-agenda">
                {sortedDates.map(dateStr => {
                    const isToday = dateStr === todayStr;
                    const isPast = dateStr < todayStr;
                    const dateObj = new Date(dateStr + 'T12:00:00');

                    return (
                        <div key={dateStr} className={`cal-agenda-group ${isToday ? 'agenda-today' : ''} ${isPast ? 'agenda-past' : ''}`}>
                            <div className="cal-agenda-date-header">
                                <span className="cal-agenda-day">
                                    {isToday ? '⚡ HOY' : dateObj.toLocaleDateString('es-PE', {
                                        weekday: 'short', day: '2-digit', month: 'short'
                                    }).toUpperCase()}
                                </span>
                                <span className="cal-agenda-count">{grouped[dateStr].length} entrega{grouped[dateStr].length !== 1 ? 's' : ''}</span>
                            </div>

                            {grouped[dateStr].map(ev => {
                                const balance = parseFloat(ev.balance || 0);
                                const priority = getPriorityLabel(ev.priority);
                                return (
                                    <div
                                        key={ev.id}
                                        className="cal-agenda-card"
                                        style={{ borderLeftColor: getStatusColor(ev.status) }}
                                        onClick={() => openEventModal(ev)}
                                    >
                                        <div className="cal-agenda-card-top">
                                            <span className="cal-agenda-order-id">#{ev.order_number}</span>
                                            <span
                                                className="cal-agenda-status"
                                                style={{ background: getStatusColor(ev.status) + '20', color: getStatusColor(ev.status) }}
                                            >
                                                {getStatusLabel(ev.status)}
                                            </span>
                                            {ev.priority && ev.priority !== 'scheduled' && (
                                                <span className="cal-agenda-priority" style={{ color: priority.color }}>
                                                    {priority.label}
                                                </span>
                                            )}
                                        </div>
                                        <div className="cal-agenda-customer">{ev.customer_name}</div>
                                        {ev.customer_phone && (
                                            <div className="cal-agenda-phone">📞 {ev.customer_phone}</div>
                                        )}
                                        <div className="cal-agenda-card-bottom">
                                            <span>Total: <strong>S/. {parseFloat(ev.total_amount || 0).toFixed(2)}</strong></span>
                                            <span className={balance > 0 ? 'cal-balance-pending' : 'cal-balance-paid'}>
                                                Saldo: S/. {balance.toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        );
    };

    // ── Render principal ──
    return (
        <div className="calendar-page">
            {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

            {/* Header */}
            <div className="orders-header">
                <div>
                    <h2>📅 Cronograma de Entregas</h2>
                    <p className="orders-header-subtitle">Visualiza y gestiona tus fechas de entrega</p>
                </div>
            </div>

            {/* KPIs */}
            <div className="cal-stats-grid">
                <div className="cal-stat-box danger">
                    <div className="cal-stat-icon">🚨</div>
                    <div className="cal-stat-info">
                        <span className="cal-stat-label">VENCIDAS</span>
                        <span className="cal-stat-value">{stats.overdue || 0}</span>
                    </div>
                </div>
                <div className="cal-stat-box warning">
                    <div className="cal-stat-icon">⚡</div>
                    <div className="cal-stat-info">
                        <span className="cal-stat-label">PARA HOY</span>
                        <span className="cal-stat-value">{stats.today || 0}</span>
                    </div>
                </div>
                <div className="cal-stat-box info">
                    <div className="cal-stat-icon">📅</div>
                    <div className="cal-stat-info">
                        <span className="cal-stat-label">MAÑANA</span>
                        <span className="cal-stat-value">{stats.tomorrow || 0}</span>
                    </div>
                </div>
                <div className="cal-stat-box success">
                    <div className="cal-stat-icon">✅</div>
                    <div className="cal-stat-info">
                        <span className="cal-stat-label">LISTOS</span>
                        <span className="cal-stat-value">{stats.ready_to_deliver || 0}</span>
                    </div>
                </div>
            </div>

            {/* Navegación de mes */}
            <div className="cal-nav-bar">
                <div className="cal-nav-group">
                    <button className="cal-nav-btn" onClick={() => navigateDate(-1)}>❮</button>
                    <button className="cal-today-btn" onClick={goToToday}>Hoy</button>
                    <button className="cal-nav-btn" onClick={() => navigateDate(1)}>❯</button>
                </div>
                <h3 className="cal-current-month">
                    {currentDate.toLocaleDateString('es-PE', { month: 'long', year: 'numeric' })}
                </h3>
                <div style={{ width: '100px' }} />
            </div>

            {/* Búsqueda y filtros */}
            <div className="cal-tools">
                <div className="cal-search-box">
                    <span className="cal-search-icon">🔍</span>
                    <input
                        type="text"
                        placeholder="Buscar pedido, cliente..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="cal-filter-pills">
                    <button className={`cal-filter-pill ${statusFilter === 'all' ? 'active' : ''}`} onClick={() => setStatusFilter('all')}>Todos</button>
                    <button
                        className={`cal-filter-pill ${statusFilter === 'pendiente' ? 'active' : ''}`}
                        style={{ borderColor: '#ffc107', color: statusFilter === 'pendiente' ? '#fff' : '#d39e00', backgroundColor: statusFilter === 'pendiente' ? '#ffc107' : 'transparent' }}
                        onClick={() => setStatusFilter('pendiente')}
                    >Pendiente</button>
                    <button
                        className={`cal-filter-pill ${statusFilter === 'en_preparacion' ? 'active' : ''}`}
                        style={{ borderColor: '#17a2b8', color: statusFilter === 'en_preparacion' ? '#fff' : '#117a8b', backgroundColor: statusFilter === 'en_preparacion' ? '#17a2b8' : 'transparent' }}
                        onClick={() => setStatusFilter('en_preparacion')}
                    >Producción</button>
                    <button
                        className={`cal-filter-pill ${statusFilter === 'listo' ? 'active' : ''}`}
                        style={{ borderColor: '#28a745', color: statusFilter === 'listo' ? '#fff' : '#1e7e34', backgroundColor: statusFilter === 'listo' ? '#28a745' : 'transparent' }}
                        onClick={() => setStatusFilter('listo')}
                    >Listo</button>
                </div>
                <button className="cal-refresh-btn" onClick={loadCalendarData}>🔄 Actualizar</button>
            </div>

            {/* Calendario / Agenda */}
            <div style={{ position: 'relative' }}>
                {isLoading && (
                    <div className="cal-loading" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10, background: 'rgba(255, 255, 255, 0.9)', padding: '1rem 2rem', borderRadius: '8px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', fontWeight: 'bold' }}>
                        ⏳ Cargando cronograma...
                    </div>
                )}
                
                <div style={{ opacity: isLoading ? 0.4 : 1, pointerEvents: isLoading ? 'none' : 'auto', transition: 'opacity 0.2s ease-in-out' }}>
                    {isMobile ? (
                        // ── MÓVIL: Vista Agenda ──
                        renderAgendaView()
                    ) : (
                        // ── PC: Vista Mensual ──
                        <>
                            <div className="cal-weekdays-header">
                                {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(d => (
                                    <div key={d} className="cal-weekday">{d}</div>
                                ))}
                            </div>
                            <div className="cal-container">
                                {renderMonthView()}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* MODAL 1: Detalle de pedido */}
            {showEventModal && selectedEvent && createPortal(
                <div className="modal-overlay" onClick={closeEventModal}>
                    <div className="modal-card" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>📦 Pedido #{selectedEvent.order_number}</h3>
                            <button className="close-btn" onClick={closeEventModal}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="cal-detail-row">
                                <div className="cal-detail-group">
                                    <label>Cliente</label>
                                    <p>{selectedEvent.customer_name}</p>
                                    {selectedEvent.customer_phone && <small>📞 {selectedEvent.customer_phone}</small>}
                                </div>
                                <div className="cal-detail-group text-right">
                                    <label>Estado</label>
                                    <span className="status-badge" style={{ background: getStatusColor(selectedEvent.status) + '20', color: getStatusColor(selectedEvent.status) }}>
                                        {getStatusLabel(selectedEvent.status)}
                                    </span>
                                </div>
                            </div>

                            {selectedEvent.event_date && (
                                <div className="cal-detail-highlight">
                                    <label>📅 Fecha de entrega</label>
                                    <p>{formatDate(selectedEvent.event_date)}</p>
                                </div>
                            )}

                            <div className="cal-detail-highlight">
                                <div>
                                    <label>Total</label>
                                    <p>S/. {parseFloat(selectedEvent.total_amount || 0).toFixed(2)}</p>
                                </div>
                                <div className="text-right">
                                    <label>Saldo Pendiente</label>
                                    <p style={{ color: parseFloat(selectedEvent.balance) > 0 ? '#dc3545' : '#28a745' }}>
                                        S/. {parseFloat(selectedEvent.balance || 0).toFixed(2)}
                                    </p>
                                </div>
                            </div>

                            {selectedEvent.notes && (
                                <div className="cal-notes-box">
                                    <label>📝 Notas</label>
                                    <p>{selectedEvent.notes}</p>
                                </div>
                            )}

                            <div className="modal-actions">
                                <button className="btn-cancel" onClick={closeEventModal}>Cerrar</button>
                                <button
                                    className="btn-action"
                                    onClick={() => navigate(`/orders`)}
                                >
                                    Ir a Pedidos ➜
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* MODAL 2: Lista del día (PC) */}
            {dayListModal.isOpen && createPortal(
                <div className="modal-overlay" onClick={closeDayListModal}>
                    <div className="modal-card" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>📅 {dayListModal.date?.toLocaleDateString('es-PE', { weekday: 'long', day: '2-digit', month: 'long' })}</h3>
                            <button className="close-btn" onClick={closeDayListModal}>×</button>
                        </div>
                        <div className="modal-body">
                            <p className="cal-day-list-subtitle">{dayListModal.events.length} entregas programadas</p>
                            <div className="cal-day-list">
                                {dayListModal.events.map(ev => (
                                    <div
                                        key={ev.id}
                                        className="cal-day-list-item"
                                        style={{ borderLeftColor: getStatusColor(ev.status) }}
                                        onClick={() => handleEventClickFromList(ev)}
                                    >
                                        <div>
                                            <strong>{ev.customer_name}</strong>
                                            <div className="cal-day-list-sub">#{ev.order_number} · S/. {parseFloat(ev.total_amount || 0).toFixed(2)}</div>
                                        </div>
                                        <span className="status-badge" style={{ background: getStatusColor(ev.status) + '20', color: getStatusColor(ev.status) }}>
                                            {getStatusLabel(ev.status)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <div className="modal-actions">
                                <button className="btn-cancel" onClick={closeDayListModal}>Cerrar</button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>  // calendar-page
    );
};

export default CalendarPage;